import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { AlertTriangleIcon } from "lucide-react";

import { AccessDenied } from "@/components/AccessDenied";

import { useAuthStore } from "@/store/auth";
import type { Role } from "@/types";
import { fetchMe } from "@/api/auth";

interface RBACGuardProps {
  children: ReactNode;
  /** If set, only these roles may render children. Otherwise any auth'd
   *  user may pass. */
  allow?: Role[];
}

/**
 * Wraps a route. S-SEC-3 — there is no JS-readable token anymore; the
 * session lives in an httpOnly cookie. We determine "logged in" by
 * probing GET /auth/me (the cookie authenticates it). Until that probe
 * resolves (`authChecked`) we render nothing to avoid flashing /login on
 * a valid-cookie reload. After it resolves: no user → /login; wrong role
 * → /.
 */
export function RBACGuard({ children, allow }: RBACGuardProps) {
  const { user, authChecked, setUser, setAuthChecked, clear } = useAuthStore();
  const location = useLocation();
  const [probeFailed, setProbeFailed] = useState(false);

  useEffect(() => {
    if (authChecked) return;
    let cancelled = false;
    fetchMe()
      .then((u) => {
        if (!cancelled) {
          setUser(u);
          setProbeFailed(false);
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // A 401 means "not signed in" — bounce to /login, correctly.
        // ANYTHING ELSE (network down, 500, CORS) means we could not ASK.
        // Bouncing those to /login tells the operator they are logged out
        // when they are not: the same "cannot check reported as checked and
        // failed" conflation this codebase has now hit five times server-side.
        const status = (e as { response?: { status?: number } })?.response?.status;
        setProbeFailed(status === undefined || status >= 500);
        clear();
      })
      .finally(() => {
        if (!cancelled) setAuthChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [authChecked, setUser, setAuthChecked, clear]);

  // THE WHITE SCREEN. This returned a bare `null`.
  //
  // Every authenticated route is a child of this guard, and Layout — the
  // sidebar, header and nav — is INSIDE it. So while the /auth/me probe was in
  // flight the entire document rendered nothing: no chrome, no text, and no
  // console error, because nothing threw. That is an exact match for the 11
  // pages re-audited as "white screen, NO SIDEBAR, NO CONSOLE ERRORS", and it
  // is the ONLY bare-null on the whole-app render path (grep confirms the
  // others are leaves — a closed drawer, a hidden nav item, an empty
  // breadcrumb).
  //
  // It is also timing-dependent, which is why the same page can be reported
  // blank by an audit and fully rendered in a browser on the same day: the
  // difference is whether the probe resolved before the screenshot.
  //
  // An ErrorBoundary cannot catch this. There is no error. A component that
  // renders nothing is invisible to every mechanism except looking at it.
  if (!authChecked) {
    return (
      <div
        data-testid="session-probe"
        className="flex min-h-screen items-center justify-center"
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="h-1.5 w-32 animate-pulse rounded-full bg-muted" />
          <p className="text-sm text-muted-foreground">Checking your session…</p>
        </div>
      </div>
    );
  }

  // The probe could not RUN. Say so, and offer a retry — do not claim the
  // operator is signed out on the strength of a request that never landed.
  if (probeFailed) {
    return (
      <div
        data-testid="session-unavailable"
        className="flex min-h-screen items-center justify-center p-6"
      >
        <div className="flex max-w-sm flex-col items-center gap-3 text-center">
          <AlertTriangleIcon className="h-6 w-6 text-destructive" />
          <p className="text-sm font-medium">Could not reach the server</p>
          <p className="text-xs text-muted-foreground">
            Your session could not be checked, so we cannot tell whether you are
            signed in. This is not a sign-out.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-1 rounded-md border border-input bg-background px-3 py-1.5 text-xs hover:bg-muted"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allow && !allow.includes(user.role)) {
    // Named, with the chrome, instead of a silent bounce to "/".
    return <AccessDenied what="This screen" needs={allow.join(" or ")} />;
  }

  return <>{children}</>;
}
